import { move } from '@go-shared/lib/functional';
import { hashStringToColor, safeIdCompare } from '@go-shared/lib/utils';
import {
  BareSimpleGroupBooking,
  GroupBooking,
  VenueRequestFile,
} from '@go-shared/types/groupBooking';
import Fuse from 'fuse.js';
import React, { useEffect, useMemo, useState } from 'react';

import {
  BubbleAccordion,
  BubbleBadge,
  BubbleButton,
  BubbleInput,
  BubbleOverlay,
  BubbleTable,
} from '../BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '../BubbleUI/BubbleTable';
import { FormGenericContainer } from '../FormGenericContainer';
import { EnterGroupPageFields } from './EnterGroupPage';
import { useGroupDetailsValidation } from '@go-client/hooks/useGroupDetailsValidation';
import { GroupForm } from './Workflows';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import FormStatusFooter from './FormStatusFooter';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandGroupType } from '@go-shared/types/brand';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { getCommonTexts } from '@go-shared/lib/brand';

interface GroupsTableItem {
  id?: string;
  groupName: string;
  groupId?: string;
  bookingNo?: string;
  groupType?: BrandGroupType;
  guestCount: number;
  _data: BareSimpleGroupBooking;
}

interface SelectGroupsPageProps {
  groupForm: GroupForm;
  changeGroupForm: <T extends keyof GroupForm>(key: T, val: GroupForm[T]) => void;
  clearGroupForm: () => void;
  isNewGroup: boolean;
  setIsNewGroup: React.Dispatch<React.SetStateAction<boolean>>;
  groupTypeOpts: BrandGroupType[];
  title?: string;
  pageIndex?: number;
  selectedGroup?: GroupBooking;
  groups: BareSimpleGroupBooking[];
  showDayContext: boolean;
  nextTitle?: string;
  onClose?: () => void;
  onSelectGroup: (group: BareSimpleGroupBooking) => Promise<void>;
  onToggleShowDayContext: () => void;
  onUpdateFile: (file: VenueRequestFile) => void;
  onUploadFile: (file: File) => void;
  onDeleteFile: (file: VenueRequestFile) => void;
  onGoNext: () => void;
}

export default function SelectGroupsPage(props: SelectGroupsPageProps) {
  const [isBusy, setIsBusy] = useState('');
  const [search, setSearch] = useState('');
  const { mandatoryFields, touchedFields, formValid, handleFieldBlur } = useGroupDetailsValidation(
    props.groupForm
  );

  useEffect(() => {
    props.changeGroupForm('name', search);
  }, [search]);

  const { activeProperty } = useActiveProperty();
  const { brand } = useServerInfo();

  const { party_name, group_id, group_type, guests } = getCommonTexts(
    {
      default: brand.commonTexts,
      translations: [],
    },
    { selected: 'en' }
  );

  const groupsTableItems: GroupsTableItem[] = useMemo(() => {
    let selectedIdx = -1;

    const allGroups = props.groups
      .filter(g => !g.archived)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .map((gb, idx) => {
        if (safeIdCompare(gb._id, props.selectedGroup?._id)) {
          selectedIdx = idx;
        }

        return {
          id: gb.party.groupId,
          groupName: gb.party.name,
          groupId: gb.party.groupId,
          bookingNo: gb.party.bookingNo,
          groupType: gb.party.groupType,
          guestCount: gb.party.size || 0,
          _data: gb,
        };
      });

    if (selectedIdx > -1) move(allGroups, selectedIdx, 0);

    return allGroups;
  }, [props.groups]);

  const fuse = useMemo(() => {
    return new Fuse(
      groupsTableItems.map(opt => ({
        text: opt.groupName,
        groupId: opt.groupId,
        bookingNo: opt.bookingNo,
        option: opt,
      })),
      {
        ...DEFAULT_FUSE_SETTINGS,
        keys: ['text', 'groupId', 'bookingNo'],
      }
    );
  }, [groupsTableItems]);

  const fuseSearchResults = useMemo(() => {
    if (!search) return undefined;
    return fuse.search(search).map(s => s.item.option);
  }, [fuse, search]);

  const filteredGroupsTableItems = fuseSearchResults || groupsTableItems;

  const groupsTableFields: Array<BubbleTableFields<GroupsTableItem>> = [
    {
      key: 'select',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    { key: 'groupName', label: party_name },
    { key: 'groupId', label: group_id },
    { key: 'groupType', label: group_type },
    { key: 'guestCount', label: guests },
  ];

  const groupsTableTemplates: Array<BubbleTableTemplate<GroupsTableItem>> = [
    {
      key: 'select',
      render: item =>
        isBusy === item._data._id ? (
          <BubbleOverlay
            hideBg
            loading
            style={{ width: '20px', height: '20px' }}
            loaderProps={{
              style: {
                width: '20px',
                height: '20px',
              },
            }}
          />
        ) : (
          <BubbleInput
            type="checkbox"
            appearance="rounded"
            inputClassName="stretched-link bg-white"
            checked={props.selectedGroup?._id === item._data._id}
            onValue={() => handleOnSelectGroup(item._data)}
          />
        ),
    },
    {
      key: 'groupType',
      render: item => item.groupType?.name,
    },
  ];

  async function handleOnSelectGroup(simpleGroup: BareSimpleGroupBooking) {
    if (!props.onSelectGroup) return;

    setIsBusy(simpleGroup._id);

    try {
      await props.onSelectGroup(simpleGroup);
    } finally {
      setIsBusy('');
    }
  }

  const hasGroupSelected = !!props.selectedGroup;
  const nextDisabled = isBusy !== '' || (props.isNewGroup ? !formValid : !hasGroupSelected);

  return (
    <div className="tflf-1">
      <FormGenericContainer
        className="h-screen flex flex-col"
        bodyClassName="flex-grow flex flex-col"
        footer={() => (
          <>
            <FormStatusFooter {...props} className="text-slate-dark mr-auto" />
            <BubbleButton
              className="ml-auto"
              disabled={nextDisabled}
              variant="navy"
              onClick={props.onGoNext}
            >
              {props.nextTitle || 'Next: Select Voyage & Venue'}
            </BubbleButton>
          </>
        )}
      >
        <div className="flex items-start mb-5">
          <div>
            <div className="flex items-center">
              <h1
                className="text-lg text-slate-dark font-semibold"
                dangerouslySetInnerHTML={{ __html: props.title || 'Create Group Booking' }}
              />
              <BubbleBadge color={hashStringToColor(activeProperty.name)} className="ml-3">
                {activeProperty.name}
              </BubbleBadge>
            </div>
            <h2 className="text-2xl text-navy">
              {props.pageIndex !== undefined ? `${props.pageIndex}. ` : ''}Select Group
            </h2>
          </div>
          <div className="ml-auto" />
          {/* <BubbleButton
            className="ml-auto mr-2"
            variant="banana"
            onClick={props.onToggleShowDayContext}
            icon={['fass', 'chart-tree-map']}
          >
            Toggle Venue Context
          </BubbleButton> */}
          <BubbleButton
            variant="navy"
            inverse
            onClick={props.onClose}
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <div className="flex-grow -mx-8 -mb-2">
          <BubbleAccordion
            variant="navy-light"
            singleMode
            noToggle
            headerClassName="px-4 py-4"
            className="h-full flex flex-col"
            open={[props.isNewGroup ? 0 : 1]}
            labels={['Create a New Group', 'Use an Existing Group']}
            onOpen={idx => props.setIsNewGroup(idx === 0)}
            headerFn={(label, open, _, idx) => (
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={open ? 'circle-dot' : 'circle'}
                  fixedWidth
                  size="lg"
                  className="mr-3"
                />
                <span className="font-semibold">{label}</span>
                <div className="ml-auto" />
                {open && idx === 0 && (
                  <BubbleButton
                    className="-my-3"
                    variant="white"
                    inverse
                    onClick={props.clearGroupForm}
                    icon="eraser"
                  >
                    Clear Form
                  </BubbleButton>
                )}
                {idx === 1 && search && (
                  <span className="font-semibold">
                    {`${filteredGroupsTableItems.length} similar group(s) found`}
                  </span>
                )}
              </div>
            )}
          >
            <div>
              <EnterGroupPageFields
                partyForm={props.groupForm}
                changePartyForm={props.changeGroupForm}
                groupTypeOpts={props.groupTypeOpts}
                onUpdateFile={props.onUpdateFile}
                onUploadFile={props.onUploadFile}
                onDeleteFile={props.onDeleteFile}
                className="my-6 px-8"
                mandatoryFields={mandatoryFields}
                touchedFields={touchedFields}
                onFieldBlur={handleFieldBlur}
              />
            </div>

            <div className="pb-2 px-8">
              <BubbleTable
                headVariant="navy"
                stickyHeader={0}
                striped
                responsive
                keyExtractor={item => item._data._id}
                items={filteredGroupsTableItems}
                fields={groupsTableFields}
                templates={groupsTableTemplates}
                showPretitle
                pretitleClassName="p-0-i"
                pretitleRender={() => (
                  <div>
                    <div className="-mx-8 bg-eggshell" style={{ height: '1.5rem' }} />
                    <div className="px-4 pt-4 mb-1">
                      <BubbleInput
                        variant="navy"
                        block
                        type="text"
                        autoFocus
                        value={search}
                        placeholder="Search by Group Name, Group ID, or Booking No."
                        onValue={setSearch}
                      />
                    </div>
                  </div>
                )}
              />
            </div>
          </BubbleAccordion>
        </div>
      </FormGenericContainer>
    </div>
  );
}
